import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://billingportal.sharavisual.com/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Convert DELETE requests to POST with _method parameter for PHP compatibility
        if (config.method === 'delete') {
            config.method = 'post';
            if (!config.data) config.data = {};
            config.data._method = 'DELETE';
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        if (response.data && typeof response.data === 'object') {
            return response.data;
        }
        return response;
    },
    (error) => {
        console.error('API Error:', error);

        if (error.response) {
            const errorMessage = error.response.data?.message ||
                error.response.data?.error ||
                `Server error: ${error.response.status}`;
            return Promise.reject(new Error(errorMessage));
        } else if (error.request) {
            return Promise.reject(new Error('Network error: No response from server'));
        } else {
            return Promise.reject(new Error(error.message || 'Request setup failed'));
        }
    }
);

// Add to your existing api.js

export const authAPI = {
    // Register new user
    register: async (userData) => {
        try {
            const isFormData = userData instanceof FormData;

            const response = await fetch(`${API_BASE_URL}/auth.php?action=register`, {
                method: 'POST',
                headers: isFormData ? {} : {
                    'Content-Type': 'application/json',
                },
                body: isFormData ? userData : JSON.stringify(userData)
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('API Register Error:', error);
            throw error;
        }
    },

    // Login
    login: async (credentials) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth.php?action=login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credentials)
            });

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('API Login Error:', error);
            throw error;
        }
    },

    // Forgot password
    forgotPassword: async (data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth.php?action=forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            return await response.json();
        } catch (error) {
            console.error('API Forgot Password Error:', error);
            throw error;
        }
    },

    // Reset password
    resetPassword: async (data) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth.php?action=reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            return await response.json();
        } catch (error) {
            console.error('API Reset Password Error:', error);
            throw error;
        }
    },

    // Change password
    changePassword: async (data, token) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth.php?action=change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(data)
            });

            return await response.json();
        } catch (error) {
            console.error('API Change Password Error:', error);
            throw error;
        }
    },

    // Get current user
    getCurrentUser: async (token) => {
        try {
            const response = await fetch(`${API_BASE_URL}/auth.php?action=profile`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            return await response.json();
        } catch (error) {
            console.error('API Get Profile Error:', error);
            throw error;
        }
    },

    // Update profile
    updateProfile: async (profileData, token) => {
        try {
            const isFormData = profileData instanceof FormData;

            const response = await fetch(`${API_BASE_URL}/auth.php?action=update-profile`, {
                method: 'POST',
                headers: isFormData ? {
                    'Authorization': `Bearer ${token}`
                } : {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: isFormData ? profileData : JSON.stringify(profileData)
            });

            return await response.json();
        } catch (error) {
            console.error('API Update Profile Error:', error);
            throw error;
        }
    }
};

// core/action/api.js

export const userAPI = {
    // Get all users with pagination and filters
    getAll: (params) => api.get('/user_list.php', { params }),

    // Get single user by ID
    get: (id) => api.get(`/user_view.php?id=${id}`),

    // Create new user
    create: (data) => api.post('/user_create.php', data),

    // Update user
    update: (data) => api.post('/user_update.php', data),

    // Delete user (soft delete)
    delete: (data) => api.post('/user_delete.php', data)
};

// core/action/api.js



export const permissionAPI = {
    // Get all permissions
    getAll: () => api.get('/permission_list.php')
};

// export const companyAPI = {
//     getSettings: () => api.get('/company_settings.php'),
//     updateSettings: (data, token) => {
//         if (data instanceof FormData) {
//             return api.post('/company_settings.php', data, {
//                 headers: { 
//                     'Authorization': `Bearer ${token}`,
//                     'Content-Type': 'multipart/form-data'
//                 }
//             });
//         }
//         return api.post('/company_settings.php', data, {
//             headers: { 'Authorization': `Bearer ${token}` }
//         });
//     }
// };
export const purchaseAPI = {
    // Create purchase
    create: (purchaseData) => api.post('/purchase_create.php', purchaseData),

    // List purchases with pagination and filters
    list: (params) => api.get('/purchase_list.php', { params }),

    // Get single purchase
    get: (id) => api.get(`/purchase_view.php?id=${id}`),

    // Update purchase
    update: (id, purchaseData) => api.put('/purchase_update.php', { id, ...purchaseData }),

    // Delete purchase
    delete: (deleteData) => api.delete('/purchase_delete.php', { data: deleteData }),

    // Get purchase items
    getItems: (purchaseId) => api.get(`/purchase_items.php?purchase_id=${purchaseId}`),

    // Create purchase item
    createItem: (itemData) => api.post('/purchase_item_create.php', itemData),

    // Delete purchase item
    deleteItem: (id) => api.delete('/purchase_item_delete.php', { data: { id } }),

    // Update purchase item
    updateItem: (id, itemData) => api.put('/purchase_item_update.php', { id, ...itemData }),

    // Add to purchaseAPI object in api.js

    // Get payment history with pagination
    getPaymentHistory: (purchaseId, page = 1, limit = 10) =>
        api.get(`/payment_history.php?purchase_id=${purchaseId}&page=${page}&limit=${limit}`),

    // Add payment
    addPayment: (paymentData) => api.post('/payment_add.php', paymentData),

    // Delete payment
    deletePayment: (id) => api.delete('/payment_delete.php', { data: { id } }),
};

// Supplier APIs
// export const supplierAPI = {
//     list: () => api.get('/supplier_list.php'),

//     // Get all suppliers for dropdown
//     getAll: () => api.get('/supplier_list.php?limit=1000'),
// };

// Add to your existing api.js file

export const supplierAPI = {
    // Get all suppliers with pagination and filters
    getAll: (params) => api.get('/supplier_list.php', { params }),

    // Get single supplier by ID
    get: (id) => api.get(`/supplier_view.php?id=${id}`),

    // Create new supplier
    create: (data) => api.post('/supplier_create.php', data),

    // Update supplier - FIXED: Using POST with data including id
    update: (data) => {
        console.log('API update call with data:', data); // Debug log
        return api.post('/supplier_update.php', data);
    },

    // Delete supplier
    delete: (deleteData) => api.delete('/supplier_delete.php', { data: deleteData }),

    // Search suppliers
    search: (params) => api.get('/supplier_search.php', { params }),

    // Check if supplier code exists
    checkCode: (params) => api.get('/supplier_check_code.php', { params }),

    // Get supplier purchase summary for reports
    getPurchaseSummary: (params) => api.get('/supplier_purchase_summary.php', { params }),

    // Get supplier payment history
    getPaymentHistory: (supplierId, params) =>
        api.get(`/supplier_payment_history.php?supplier_id=${supplierId}`, { params })
};

export const productAPI = {
    // Create product
    create: async (productData) => {
        // Convert images to base64 for JSON transmission
        const processedData = { ...productData };

        if (processedData.images && Array.isArray(processedData.images)) {
            const base64Images = [];
            for (const imgFile of processedData.images) {
                if (imgFile instanceof File) {
                    try {
                        const base64 = await convertFileToBase64(imgFile);
                        base64Images.push(base64);
                    } catch (error) {
                        console.error('Error converting file to base64:', error);
                    }
                } else {
                    // If it's already a base64 string or URL
                    base64Images.push(imgFile);
                }
            }
            processedData.images = base64Images;
        }

        return api.post('/product_create.php', processedData);
    },

    // Search products - NEW METHOD ADDED
    // search: async (searchTerm, limit = 20) => {
    //     try {
    //         const params = { 
    //             search: searchTerm,
    //             limit: limit
    //         };

    //         console.log('Searching products with params:', params);
    //         const response = await api.get('/product_search.php', { params });
    //         console.log('Search response:', response);

    //         return response;
    //     } catch (error) {
    //         console.error('Error searching products:', error);
    //         return {
    //             success: false,
    //             message: error.message || 'Failed to search products',
    //             data: []
    //         };
    //     }
    // },

    search: (searchTerm) => {
        // Use the dedicated search endpoint with no limit
        return api.get(`/product_search.php?search=${encodeURIComponent(searchTerm)}&no_limit=true`);
    },


    // List products
    list: (params = {}) => {
        const defaultParams = {

            ...params
        };

        const queryParams = new URLSearchParams();
        Object.keys(defaultParams).forEach(key => {
            if (defaultParams[key] !== null && defaultParams[key] !== undefined) {
                queryParams.append(key, defaultParams[key]);
            }
        });

        return api.get(`/product_list.php?${queryParams.toString()}`);
    },

    // Get single product
    getSingle: async (id) => {
        try {
            const response = await api.get(`/productSingle.php`, {
                params: { id }
            });
            return response;
        } catch (error) {
            console.error('API Error - Get single product:', error);
            throw error;
        }
    },

    // Update product (no FormData)
    update: async (id, productData) => {
        // Convert new images to base64
        const processedData = {
            id,
            ...productData
        };

        if (processedData.new_images && Array.isArray(processedData.new_images)) {
            const base64Images = [];
            for (const imgFile of processedData.new_images) {
                if (imgFile instanceof File) {
                    try {
                        const base64 = await convertFileToBase64(imgFile);
                        base64Images.push(base64);
                    } catch (error) {
                        console.error('Error converting file to base64:', error);
                    }
                } else {
                    base64Images.push(imgFile);
                }
            }
            processedData.new_images = base64Images;
        }

        // Process existing images - extract just filenames from paths
        if (processedData.existing_images && Array.isArray(processedData.existing_images)) {
            processedData.existing_images = processedData.existing_images.map(img => {
                if (typeof img === 'string') {
                    // If it's a full URL/path, extract just filename
                    if (img.includes('/')) {
                        return img.split('/').pop();
                    }
                    return img;
                }
                return img;
            });
        }

        return api.put('/product_update.php', processedData);
    },

    // Update product price and quantity only (admin)
    updatePriceQty: (id, payload) => api.put('/product_update.php', { id, ...payload }),

    // Delete product - Use POST with JSON
    delete: (id) => api.delete('/product_delete.php', { data: { id } }),

    // Check SKU exists
    checkSku: (sku) => api.get(`/product_check_sku.php?sku=${sku}`),

    // Check item code exists
    checkItemCode: (itemCode) => api.get(`/product_check_item_code.php?item_code=${itemCode}`),
    // Get categories
    getCategories: () => api.get('/category_dropdown.php?limit=1000'),

    // Get brands
    getBrands: () => api.get('/brand_dropdown.php?limit=1000'),

    // Get units
    getUnits: () => api.get('/unit_dropdown.php?limit=1000'),
    // Get last sale price for customer-product
    getLastPrice: (customerId, productId) =>
        api.get(`/get_product_last_price.php?customer_id=${customerId}&product_id=${productId}`)
};

// Master data APIs
export const masterAPI = {
    // Get categories
    getCategories: () => api.get('/category_list.php?limit=1000'),

    // Get brands
    getBrands: () => api.get('/brand_list.php?limit=1000'),

    // Get units
    getUnits: () => api.get('/unit_list.php?limit=1000'),

    // Get tax rates
    getTaxRates: () => api.get('/tax_rates.php?limit=1000'),

    // Get all master data in one call (for form dropdowns)
    getAll: () => api.get('/master_data.php'),
};

// Unit APIs
export const unitAPI = {
    create: (unitData) => api.post('/unit_create.php', unitData),
    list: (params) => api.get('/unit_list.php', { params }),
    get: (id) => api.get(`/unit_get.php?id=${id}`),
    update: (id, unitData) => api.put('/unit_update.php', { id, ...unitData }),
    delete: (id) => api.delete('/unit_delete.php', { data: { id } }),
};

// Brand APIs
export const brandAPI = {
    create: (brandData) => api.post('/brand_create.php', brandData),
    list: (params) => api.get('/brand_list.php', { params }),
    get: (id) => api.get('/brand_get.php', { params: { id } }),
    update: (id, brandData) => api.put('/brand_update.php', { id, ...brandData }),
    delete: (id) => api.delete('/brand_delete.php', { data: { id } }),
    search: (searchTerm, params = {}) => {
        const searchParams = { search: searchTerm, ...params };
        return api.get('/brand_list.php', { params: searchParams });
    },
    getAll: () => api.get('/brand_list.php', { params: { limit: 1000 } }),
};

// Tax APIs
export const taxAPI = {
    create: (taxData) => api.post('/tax_create.php', taxData),
    list: (params) => api.get('/tax_list.php', { params }),
    get: (id) => api.get(`/tax_get.php?id=${id}`),
    update: (id, taxData) => api.put('/tax_update.php', { id, ...taxData }),
    delete: (id) => api.delete('/tax_delete.php', { data: { id } }),
};

// Category APIs
export const categoryAPI = {
    create: (categoryData) => api.post('/category_create.php', categoryData),
    list: (params = {}) => {
        const queryString = new URLSearchParams(params).toString();
        return api.get(`/category_list.php?${queryString}`);
    },
    get: (id) => api.get(`/category_get.php?id=${id}`),
    update: (id, categoryData) => api.put('/category_update.php', { id, ...categoryData }),
    delete: (id) => api.delete('/category_delete.php', { data: { id } }),
    getParentCategories: () => api.get('/category_list.php?parent_id=null'),
};

// Inventory/Stock APIs
export const inventoryAPI = {
    // Update stock after purchase
    updateStockPurchase: (stockData) => api.post('/inventory_update_purchase.php', stockData),

    // Get stock history
    getStockHistory: (productId) => api.get(`/inventory_history.php?product_id=${productId}`),

    // Get low stock alerts
    getLowStock: () => api.get('/inventory_low_stock.php'),

    // Adjust stock manually
    adjustStock: (adjustmentData) => api.post('/inventory_adjust.php', adjustmentData),
};

export const purchaseReturnAPI = {
    // Create purchase return
    create: (returnData) => api.post('/purchase_return_create.php', returnData),

    // List purchase returns
    list: (params) => api.get('/purchase_returns_list.php', { params }),

    // Get single purchase return
    get: (id) => api.get(`/purchase_return_view.php?id=${id}`),

    // Update purchase return
    update: (id, returnData) => api.put('/purchase_return_update.php', { id, ...returnData }),

    // Delete purchase return
    delete: (id) => api.delete('/purchase_return_delete.php', { data: { id } }),

    // Get purchases by supplier for returns
    getPurchasesBySupplier: (supplierId) => api.get(`/purchases_for_return.php?supplier_id=${supplierId}`),

    // Get purchase items for return
    getPurchaseItems: (purchaseId) => api.get(`/purchase_items_for_return.php?purchase_id=${purchaseId}`),

    // Create supplier credit
    createCredit: (returnId) => api.post('/supplier_credit_create.php', { return_id: returnId }),
};

export const purchaseReportAPI = {
    // Get purchase order report
    getReport: (params = {}) => {
        const queryParams = new URLSearchParams();

        Object.keys(params).forEach(key => {
            if (params[key] !== null && params[key] !== undefined) {
                queryParams.append(key, params[key]);
            }
        });

        return api.get(`/purchase_report.php?${queryParams.toString()}`);
    },

    // Get suppliers for dropdown
    getSuppliers: () => {
        return api.get('/purchase_report_suppliers.php');
    },

    // Export report
    exportReport: (exportData) => {
        return api.post('/purchase_report_export.php', exportData);
    },

    // Print report
    printReport: (filters) => {
        return api.post('/purchase_report_print.php', filters);
    }
};
// Helper function to convert File to base64
const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            // Remove the data URL prefix (e.g., "data:image/png;base64,")
            const base64 = reader.result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};
// Export the api instance as default

export const stockAPI = {
    // Get stock summary
    getSummary: (params) => {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== '') {
                queryParams.append(key, params[key]);
            }
        });
        return api.get(`/stock_summary.php?${queryParams.toString()}`);
    },

    // Get stock movements
    getMovements: (params) => {
        const queryParams = new URLSearchParams();
        Object.keys(params).forEach(key => {
            if (params[key] !== undefined && params[key] !== '') {
                queryParams.append(key, params[key]);
            }
        });
        return api.get(`/stock_movements.php?${queryParams.toString()}`);
    },

    // Adjust stock
    adjustStock: (adjustmentData) => api.post('/stock_adjust.php', adjustmentData),

    // Get low stock alerts
    getLowStock: () => api.get('/stock_low.php')
};

// Add these to your api.js file


export const customerAPI = {
    getAll: (params) => api.get('/customer_list.php', { params }),
    get: (id) => api.get(`/customer_view.php?id=${id}`),
    create: (data) => api.post('/customer_create.php', data),
    update: (id, data) => api.put('/customer_update.php', { id, ...data }),
    delete: (id) => api.delete('/customer_delete.php', { data: { id } }),
    search: (params) => api.get('/customer_search.php', { params })
};
//Sales APIs
// Add these to your api.js file

export const salesAPI = {
    // Create sale
    create: (saleData) => api.post('/sale_create.php', saleData),

    // List sales with pagination and filters
    list: (params) => api.get('/sale_list.php', { params }),

    // Get single sale
    get: (id) => api.get(`/sale_view.php?id=${id}`),

    // Update sale
    update: (id, saleData) => api.put('/sale_update.php', { id, ...saleData }),

    // Delete sale
    delete: (deleteData) => api.delete('/sale_delete.php', { data: deleteData }),

    // Get sale items
    getItems: (saleId) => api.get(`/sale_items.php?sale_id=${saleId}`),

    // Create sale item
    createItem: (itemData) => api.post('/sale_item_create.php', itemData),

    // Delete sale item
    deleteItem: (id) => api.delete('/sale_item_delete.php', { data: { id } }),

    // Update sale item
    updateItem: (id, itemData) => api.put('/sale_item_update.php', { id, ...itemData }),

    // Get payment history with pagination
    getPaymentHistory: (saleId, page = 1, limit = 10) =>
        api.get(`/sale_payment_history.php?sale_id=${saleId}&page=${page}&limit=${limit}`),

    // Add payment
    addPayment: (paymentData) => api.post('/sale_payment_add.php', paymentData),

    // Delete payment
    deletePayment: (id) => api.delete('/sale_payment_delete.php', { data: { id } }),

    // Get invoice for printing
    getInvoice: (id) => api.get(`/sale_invoice.php?id=${id}`),

    // Update status
    updateStatus: (id, status) => api.put('/sale_update_status.php', { id, status }),

    // Update payment status
    updatePaymentStatus: (id, paymentStatus, paidAmount) =>
        api.put('/sale_update_payment.php', { id, payment_status: paymentStatus, paid_amount: paidAmount })
};
export const salesReturnsAPI = {
    // Create sale return
    create: (returnData) => api.post('/sale_return_create.php', returnData),

    // List sale returns with pagination and filters
    list: (params) => api.get('/sale_return_list.php', { params }),

    // Get single sale return
    get: (id) => api.get(`/sale_return_view.php?id=${id}`),

    // Update sale return
    update: (id, returnData) => api.put('/sale_return_update.php', { id, ...returnData }),

    // Update sale return status
    updateStatus: (id, status) => api.put('/sale_return_update_status.php', { id, status }),

    // Delete sale return
    delete: (deleteData) => api.delete('/sale_return_delete.php', { data: deleteData }),

    // Get return items
    getItems: (returnId) => api.get(`/sale_return_items.php?return_id=${returnId}`),

    // Get original sale items for return
    getSaleItemsForReturn: (saleId) => api.get(`/sale_items_for_return.php?sale_id=${saleId}`),

    // Process refund
    processRefund: (refundData) => api.post('/sale_return_refund.php', refundData),

    // Search sales by invoice for returns
    searchSalesByInvoice: (params) => api.get('/sale_search_by_invoice.php', { params }),

    // Get customer credits
    getCustomerCredits: (customerId) => api.get(`/customer_credits.php?customer_id=${customerId}`)

};
export const quotationsAPI = {
    // Create quotation
    create: (quotationData) => api.post('/quotation_create.php', quotationData),

    // List quotations with pagination and filters
    list: (params) => api.get('/quotation_list.php', { params }),

    // Get single quotation
    get: (id) => api.get(`/quotation_view.php?id=${id}`),

    // Update quotation
    update: (id, quotationData) => api.put('/quotation_update.php', { id, ...quotationData }),

    // Delete quotation
    delete: (deleteData) => api.delete('/quotation_delete.php', { data: deleteData }),

    // Get quotation items
    getItems: (quotationId) => api.get(`/quotation_items.php?quotation_id=${quotationId}`),

    // Convert quotation to sale
    convertToSale: (id) => api.post('/quotation_convert.php', { id }),

    // Update status
    updateStatus: (id, status) => api.put('/quotation_update_status.php', { id, status })
};
export const bankAccountsAPI = {
    // Bank Account CRUD
    create: (accountData) => api.post('/bank_account_create.php', accountData),
    list: (params) => api.get('/bank_account_list.php', { params }),
    get: (id) => api.get(`/bank_account_view.php?id=${id}`),
    update: (id, accountData) => api.post('/bank_account_update.php', { id, ...accountData, _method: 'PUT' }),
    delete: (deleteData) => api.delete('/bank_account_delete.php', { data: deleteData }),

    // Transactions
    getTransactions: (accountId, params) =>
        api.get(`/bank_transaction_list.php?account_id=${accountId}`, { params }),
    addTransaction: (transactionData) => api.post('/bank_transaction_add.php', transactionData),
    updateTransaction: (id, transactionData) =>
        api.put('/bank_transaction_update.php', { id, ...transactionData }),
    deleteTransaction: (id) => api.delete('/bank_transaction_delete.php', { data: { id } }),

    // Transfers
    transfer: (transferData) => api.post('/bank_transfer.php', transferData),
    getTransfers: (params) => api.get('/bank_transfer_list.php', { params }),

    // Reconciliation
    reconcile: (reconcileData) => api.post('/bank_reconcile.php', reconcileData),
    getReconciliationReport: (accountId, params) =>
        api.get(`/bank_reconciliation.php?account_id=${accountId}`, { params }),

    // Dashboard/Summary
    getSummary: () => api.get('/bank_summary.php'),
    getStatement: (accountId, params) =>
        api.get(`/bank_statement.php?account_id=${accountId}`, { params }),
    getTransactionSummary: (params) => api.get('/bank_transaction_summary.php', { params }),

};
export const balanceSheetAPI = {
    get: (params) => api.get('/balance_sheet.php', { params }),
    exportPDF: (params) => api.get('/balance_sheet_export_pdf.php', { params, responseType: 'blob' }),
    exportExcel: (params) => api.get('/balance_sheet_export_excel.php', { params, responseType: 'blob' })
};
// Add to your existing api.js
export const trialBalanceAPI = {
    get: (params) => api.get('/trial_balance.php', { params }),
    exportPDF: (params) => api.get('/trial_balance_export_pdf.php', { params, responseType: 'blob' }),
    exportExcel: (params) => api.get('/trial_balance_export_excel.php', { params, responseType: 'blob' })
};
// Add to your existing api.js
export const accountStatementAPI = {
    get: (params) => api.get('/account_statement.php', { params }),
    exportPDF: (params) => api.get('/account_statement_export_pdf.php', { params, responseType: 'blob' }),
    exportExcel: (params) => api.get('/account_statement_export_excel.php', { params, responseType: 'blob' }),
    getAccounts: () => api.get('/bank_account_list.php?limit=100')
};
// In your api.js, ensure expenseAPI is properly configured
// In api.js - Make sure expenseAPI.create is set up correctly
// In api.js
export const expenseAPI = {
    list: (params) => api.get('/expense_list.php', { params }),
    get: (id) => api.get(`/expense_get.php?id=${id}`),
    create: (data) => {
        // If data is FormData, don't set Content-Type header (browser will set it with boundary)
        if (data instanceof FormData) {
            return api.post('/expense_create.php', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        }
        // Otherwise send as JSON
        return api.post('/expense_create.php', data, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },
    update: (id, data) => {
        // If data is FormData
        if (data instanceof FormData) {
            // Ensure id is in the FormData
            if (!data.has('id')) {
                data.append('id', id);
            }
            return api.post('/expense_update.php', data, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        }
        // Regular JSON data
        return api.post('/expense_update.php', { id, ...data }, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
    },
    delete: (data) => api.post('/expense_delete.php', data),
    getCategories: () => api.get('/expense_category_list.php')
};
// Add to your existing api.js
export const expenseCategoryAPI = {
    list: (params) => api.get('/expense_category_list.php', { params }),
    get: (id) => api.get(`/expense_category_get.php?id=${id}`),
    create: (data) => api.post('/expense_category_create.php', data),
    update: (id, data) => api.post('/expense_category_update.php', { id, ...data }),
    delete: (data) => api.post('/expense_category_delete.php', data)
};
// Add to your existing api.js
export const reportAPI = {
    sales: (params) => api.get('/report_sales.php', { params }),
    purchases: (params) => api.get('/report_purchases.php', { params }),
    inventory: (params) => api.get('/report_inventory.php', { params }),
    suppliers: (params) => api.get('/report_suppliers.php', { params }),
    customers: (params) => api.get('/report_customers.php', { params }),
    expenses: (params) => api.get('/report_expenses.php', { params }),
    income: (params) => api.get('/report_income.php', { params }),
    tax: (params) => api.get('/report_tax.php', { params }),
    profitLoss: (params) => api.get('/report_profit_loss.php', { params }),
    openingBalance: (params) => api.get('/report_opening_balance.php', { params }),
    exportPDF: (reportType, params) => api.get(`/report_export_pdf.php?type=${reportType}`, { params, responseType: 'blob' }),
    exportExcel: (reportType, params) => api.get(`/report_export_excel.php?type=${reportType}`, { params, responseType: 'blob' })
};
// Add to your existing api.js
export const openingBalanceAPI = {
    saveBank: (data) => api.post('/opening_balance_entry.php?type=bank', data),
    saveCustomer: (data) => api.post('/opening_balance_entry.php?type=customer', data),
    saveSupplier: (data) => api.post('/opening_balance_entry.php?type=supplier', data),
    saveProduct: (data) => api.post('/opening_balance_entry.php?type=product', data),
    getSummary: (financialYear) => api.get(`/opening_balance_entry.php?financial_year=${financialYear || ''}`),
    getHistory: (params) => api.get('/opening_balance_entry.php?action=history', { params }),
    updateEntry: (id, data) => api.post('/opening_balance_entry.php?action=update', { id, ...data, _method: 'PUT' })
};
export const dashboardAPI = {
    // Main dashboard data
    get: (params) => api.get('/dashboard_api.php', { params }),

    // Additional dashboard endpoints
    getRecentTransactions: (params) => api.get('/dashboard_recent_transactions.php', { params }),
    getTopSellingProducts: (params) => api.get('/dashboard_top_products.php', { params }),
    getLowStockProducts: (params) => api.get('/dashboard_low_stock.php', { params }),
    getSalesStatistics: (params) => api.get('/dashboard_sales_stats.php', { params }),
    getCustomerOverview: (params) => api.get('/dashboard_customer_overview.php', { params }),
    getTopCustomers: (params) => api.get('/dashboard_top_customers.php', { params })
};
// Add to your existing api.js
export const salesDashboardAPI = {
    get: (params) => api.get('/sales_dashboard_api.php', { params }),
    getPersonInfo: (id) => api.get(`/sales_person_info.php?id=${id}`),
    getTasks: (params) => api.get('/sales_tasks.php', { params }),
    addTask: (data) => api.post('/sales_tasks.php', data),
    updateTaskStatus: (id, status) => api.put(`/sales_tasks.php?id=${id}`, { status })
};
// Add to your existing api.js

export const companyAPI = {
    // Get company settings
    getSettings: () => api.get('/company_settings.php'),

    // Update company settings (with possible file uploads)
    updateSettings: (data, token) => {
        if (data instanceof FormData) {
            return api.post('/company_settings.php', data, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
        }
        return api.post('/company_settings.php', data, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
    },

    // Upload logo only
    uploadLogo: (formData, token) => api.post('/company_upload_logo.php', formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    }),

    // Upload favicon only
    uploadFavicon: (formData, token) => api.post('/company_upload_favicon.php', formData, {
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
        }
    }),

    // Delete logo
    deleteLogo: (token) => api.delete('/company_delete_logo.php', {
        headers: { 'Authorization': `Bearer ${token}` }
    }),

    // Delete favicon
    deleteFavicon: (token) => api.delete('/company_delete_favicon.php', {
        headers: { 'Authorization': `Bearer ${token}` }
    })
};
// core/action/api.js

// core/action/api.js - Add these to your existing file

export const barcodeAPI = {
    // Generate barcode for a product
    generateBarcode: (productId) => api.get(`/barcode_generate.php?action=generate&product_id=${productId}`),

    // Generate QR code for a product
    generateQR: (productId) => api.get(`/barcode_generate.php?action=qr&product_id=${productId}`),

    // Generate GST QR code for an invoice
    generateGSTQR: (saleId) => api.get(`/barcode_generate.php?action=gst-qr&sale_id=${saleId}`),

    // Create print job for multiple products
    createPrintJob: (data) => api.post('/barcode_generate.php?action=print', data),

    // Get barcodes for products
    getBarcodes: (params) => api.get('/barcode_list.php', { params }),

    // Get print jobs
    getPrintJobs: (params) => api.get('/print_jobs.php?action=list', { params }),

    // Get print job details
    getPrintJobDetails: (jobId) => api.get(`/print_jobs.php?action=details&job_id=${jobId}`),

    // Cancel print job
    cancelPrintJob: (jobId) => api.post('/print_jobs.php?action=cancel', { job_id: jobId }),

    // Get barcode settings
    getBarcodeSettings: () => api.get('/barcode_settings.php'),

    // Update barcode settings
    updateBarcodeSettings: (data) => api.post('/barcode_settings.php', data)
};
export const roleAPI = {
    // Get all roles
    getAll: (params) => api.get('/role_list.php', { params }),

    // Create new role
    create: (data) => api.post('/role_create.php', data),

    // Update role
    update: (data) => api.post('/role_update.php', data),

    // Delete role
    delete: (data) => api.post('/role_delete.php', data),

    // Get permissions for role
    getPermissions: (roleId) => api.get(`/role_permissions.php?role_id=${roleId}`),

    // Update permissions for role - NEW METHOD
    updatePermissions: (roleId, permissionIds) => {
        return api.post('/role_update_permissions.php', {
            role_id: roleId,
            permission_ids: permissionIds
        });
    }
};

// ==================== MANUFACTURING API ====================
export const manufacturingAPI = {
    // Raw Material Dispatch
    getDispatches: (params) => api.get('/dispatch_list.php', { params }),
    getDispatch: (id) => api.get(`/dispatch_view.php?id=${id}`),
    createDispatch: (data) => api.post('/dispatch_create.php', data),
    updateDispatchStatus: (id, status) => api.post('/dispatch_update_status.php', { id, status }),

    // Raw Material Receive
    getReceives: (params) => api.get('/receive_list.php', { params }),
    getReceive: (id) => api.get(`/receive_view.php?id=${id}`),
    createReceive: (data) => api.post('/receive_create.php', data),

    // Production
    getProductions: (params) => api.get('/production_list.php', { params }),
    getProduction: (id) => api.get(`/production_view.php?id=${id}`),
    createProduction: (data) => api.post('/production_create.php', data),
    updateProductionStatus: (id, status) => api.post('/production_update_status.php', { id, status }),
    updateProduction: (id, data) => api.post('/production_update.php', data),
    deleteProduction: (id) => api.post('/production_delete.php', { id }),

    // Stock Transfer
    getTransfers: (params) => api.get('/transfer_list.php', { params }),
    getTransfer: (id) => api.get(`/transfer_view.php?id=${id}`),
    createTransfer: (data) => api.post('/transfer_create.php', data),
    updateTransfer: (id, data) => api.post('/transfer_update.php', { id, ...data }),
    updateTransferStatus: (id, status) => api.post('/transfer_update_status.php', { id, status }),


    // Waste Entry
    getWasteEntries: (params) => api.get('/waste_list.php', { params }),
    getWasteEntry: (id) => api.get(`/waste_view.php?id=${id}`),
    createWasteEntry: (data) => api.post('/waste_create.php', data),
    updateWasteEntry: (productionId, data) => api.post('/waste_update.php', { production_id: productionId, ...data }),
    deleteWasteEntry: (productionId) => api.post('/waste_delete.php', { production_id: productionId }),



    // Convert to Purchase
    convertToPurchase: (data) => api.post('/convert_to_purchase.php', data),
    getUnconvertedProductions: (params) => api.get('/unconverted_productions.php', { params }),

    // Reports
    getManufacturingSummary: (params) => api.get('/manufacture_summary_report.php', { params }),
    getProductionReport: (params) => api.get('/production_report.php', { params }),
    getWasteReport: (params) => api.get('/waste_report.php', { params }),

    // Stock Views
    getRawMaterialStock: (params) => api.get('./raw_material_stock.php', { params }),
    getFinishedGoodsStock: (params) => api.get('./finished_goods_stock.php', { params }),
    getManufacturingStockSummary: (params) => api.get(`/manufacture_stock_summary.php?${params}`),
};
// ==================== RAW MATERIAL API ====================
export const rawMaterialAPI = {
    getAll: (params) => api.get('/raw_material_list.php', { params }),
    getStock: (params) => api.get('/raw_material_stock.php', { params }),
    get: (id) => api.get(`/raw_material_view.php?id=${id}`),
    create: (data) => api.post('/raw_material_create.php', data),
    update: (id, data) => api.post('/raw_material_update.php', { id, ...data }),
    delete: (id) => api.post('/raw_material_delete.php', { id }),
    createPurchase: (data) => api.post('/raw_material_purchase_create.php', data),
    getPurchases: (params) => api.get('/raw_material_purchase_list.php', { params }),
    getPurchase: (id) => api.get(`/raw_material_purchase_view.php?id=${id}`),
    updatePurchase: (id, data) => api.post('/raw_material_purchase_update.php', { id, ...data }),
    deletePurchase: (id) => api.post('/raw_material_purchase_delete.php', { id }),
    getManufacturingStock: (params) => api.get(`/raw_material_manufacturing_stock.php?${params}`),
};
export default api;

